/**
 * Auth configuration: BFF (ezy-bff) only.
 * Auth requests go to PRD_BFF_URL. The BFF adds the backend API key server-side; the frontend does not send one.
 * Local: .env. Production: /config.json from server.
 */

type RuntimeAuthConfig = {
  PRD_BFF_URL?: string;
  GOOGLE_OAUTH_CLIENT_ID?: string;
  APPLE_CLIENT_ID?: string;
  /** Apple Sign in with Apple: must match Return URL in Apple Developer exactly (e.g. with/without trailing slash). */
  APPLE_REDIRECT_URI?: string;
  /** Google Analytics 4 measurement ID (e.g. G-0K07RMB1ZR). Set on Cloud Run; server.cjs exposes it via /config.json. */
  GA_MEASUREMENT_ID?: string;
};

let runtimeConfig: RuntimeAuthConfig | null = null;

/** Called at app startup when /config.json is loaded (production). Overrides build-time env. */
export function setRuntimeAuthConfig(config: RuntimeAuthConfig) {
  runtimeConfig = config;
}

/**
 * Resolves the GA4 measurement ID. Runtime `/config.json` (Cloud Run env) takes precedence; falls
 * back to the build-time Vite env (`VITE_PRD_GA_MEASUREMENT_ID`) so local dev with `.env` keeps working.
 * Returns `undefined` when unset or malformed, so callers skip injecting gtag entirely.
 */
export function getRuntimeGAMeasurementId(): string | undefined {
  const raw = (runtimeConfig?.GA_MEASUREMENT_ID ?? import.meta.env.VITE_PRD_GA_MEASUREMENT_ID ?? "").trim();
  if (!raw) return undefined;
  return /^G-[A-Z0-9]+$/i.test(raw) ? raw : undefined;
}

function normalizeBaseUrl(url: string): string {
  const trimmed = url.replace(/\/$/, "");
  if (trimmed && !/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

function getBuildTimeConfig() {
  const baseUrl = normalizeBaseUrl(import.meta.env.PRD_BFF_URL ?? "");
  return {
    baseUrl,
    googleClientId: (import.meta.env.PRD_GOOGLE_OAUTH_CLIENT_ID ?? "").trim(),
    appleClientId: (import.meta.env.PRD_APPLE_CLIENT_ID ?? "").trim(),
    appleRedirectUri: (import.meta.env.PRD_APPLE_REDIRECT_URI ?? "").trim(),
  };
}

export function getAuthConfig() {
  const build = getBuildTimeConfig();
  if (runtimeConfig) {
    const baseUrl = normalizeBaseUrl(runtimeConfig.PRD_BFF_URL ?? "");
    const googleClientId = (runtimeConfig.GOOGLE_OAUTH_CLIENT_ID ?? build.googleClientId).trim();
    const appleClientId = (runtimeConfig.APPLE_CLIENT_ID ?? build.appleClientId).trim();
    const appleRedirectUri = (runtimeConfig.APPLE_REDIRECT_URI ?? build.appleRedirectUri).trim();
    return { baseUrl, googleClientId, appleClientId, appleRedirectUri };
  }
  return {
    ...build,
    googleClientId: build.googleClientId,
    appleClientId: build.appleClientId,
    appleRedirectUri: build.appleRedirectUri,
  };
}

/** Log which auth API the app is using (for debugging). Call once after config is ready. */
export function logAuthConfigSummary(): void {
  const c = getAuthConfig();
  if (!c.baseUrl) {
    console.log("[auth] No auth base URL configured (PRD_BFF_URL)");
    return;
  }
  console.log(`[auth] Auth API (BFF): ${c.baseUrl}`);
}
